import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import {
  ButtonParams,
  EsriErrorType,
  EsriRequestError,
  LegendParams,
  Plugin,
  PluginService
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  retry,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  timer
} from 'rxjs';
import { TwcVectorTileDataService } from './data-protocol-services/twc-vector-tile.data.service';
import { TwcWebTileDataService } from './data-protocol-services/twc-web-tile.data.service';
import {
  TwcParams,
  TwcResponseData,
  TwcUpdateData
} from './data-protocol-services/twc.data.types';
import { TwcGeoJSONPluginService } from './layer-type-services/geo-json/twc-geo-json.layer.service';
import { TwcGraphicsLayerService } from './layer-type-services/graphics/twc-graphics.layer.service';
import { TwcVectorTileLayerService } from './layer-type-services/vector-tile/twc-vector-tile.layer.service';
import { TwcGeoJSONLayer } from './layer-type-services/geo-json/twc-geo-json.layer';
import { TwcGraphicsLayer } from './layer-type-services/graphics/twc-graphics.layer';
import { TwcGroupLayer } from './layer-type-services/group/twc-group.layer';
import { TwcVectorTileLayer } from './layer-type-services/vector-tile/twc-vector-tile.layer';
import { TwcWebTileLayer } from './layer-type-services/web-tile/twc-web-tile.layer';
import { TwcWebTileLayerService } from './layer-type-services/web-tile/twc-web-tile.layer.service';
import { TwcProduct, TwcProtocol } from './twc-products';
import { TwcTimeValidationService } from './twc-time-validation.service';
import {
  TwcLayerType,
  initialFlightLevel,
  initialTime,
  normalizeSelectedFlightLevel,
  normalizeSelectedTime
} from './twc.mixin';
import { buildPauseOnVisiblitilyTsInterval$ } from './twc.utils';

/**
 * Valid layer types supported by the TWC plugin
 */
const TWC_LAYER_TYPES = [
  'geojson',
  'graphics',
  'vector-tile',
  'web-tile'
] as const;
type ValidLayerType = (typeof TWC_LAYER_TYPES)[number];

/**
 * Union type of all TWC specific layer implementations
 */
export type TwcSpecificLayer =
  | TwcGeoJSONLayer
  | TwcGraphicsLayer
  | TwcVectorTileLayer
  | TwcWebTileLayer;

/**
 * Configuration options for the TWC plugin
 */
export interface PluginOptions {
  /** Layer types to create */
  twcLayerTypes: ValidLayerType[];
  /** Optional legend configuration */
  legend?: LegendParams;
  /** Optional button configuration */
  button?: ButtonParams;
  /** Maximum number of retry attempts for failed requests (default: 3) */
  maxRetryAttempts?: number;
  /** Whether to enable debug logging (default: false) */
  enableDebugLogging?: boolean;
}

/**
 * Interface for TWC data service providers
 */
export interface TwcDataServiceInterface<D extends TwcResponseData> {
  getTwcData$(twcParams: TwcParams): Observable<D>;
}

/**
 * Interface for TWC layer service providers
 */
export interface TwcLayerServiceInterface<L extends TwcLayerType> {
  createLayer$(twcUpdateData: TwcResponseData): Observable<L>;
  updateLayer(layer: L, twcUpdateData: TwcUpdateData): L;
}

/**
 * Mapping of layer types to their corresponding class types
 */
type LayerTypeMap = {
  geojson: TwcGeoJSONLayer;
  graphics: TwcGraphicsLayer;
  'vector-tile': TwcVectorTileLayer;
  'web-tile': TwcWebTileLayer;
};

/**
 * Mapping of layer types to their corresponding service types
 */
type ServiceMap = {
  [K in ValidLayerType]: TwcLayerServiceInterface<LayerTypeMap[K]>;
};

/**
 * Service responsible for managing TWC refresh layers
 */
@Injectable({
  providedIn: 'root'
})
export class TwcRefreshLayerPluginService implements PluginService {
  id = 'twc-refresh-layer' as const;

  private readonly _ToolkitApiService = inject(ToolkitApiService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _logger = inject(NGXLogger);
  private readonly _timeValidationService = inject(TwcTimeValidationService);

  /** Map of refresh operations by product ID */
  private readonly _refreshOperations = new Map<
    string,
    BehaviorSubject<void>
  >();

  /** Default number of retry attempts for failed requests */
  private readonly DEFAULT_MAX_RETRY_ATTEMPTS = 3;

  /**
   * Map of data protocol services by protocol type
   */
  private readonly _twcDataProtocolService: Record<
    TwcProtocol,
    TwcDataServiceInterface<TwcResponseData>
  > = {
    WebTile: inject(TwcWebTileDataService),
    VectorTile: inject(TwcVectorTileDataService)
  };

  /**
   * Map of layer services by layer type
   */
  private readonly _twcArcgisType2PluginServiceMap: ServiceMap = {
    geojson: inject(TwcGeoJSONPluginService),
    graphics: inject(TwcGraphicsLayerService),
    'vector-tile': inject(TwcVectorTileLayerService),
    'web-tile': inject(TwcWebTileLayerService)
  };

  /**
   * Get the appropriate service for a layer type
   * @param layerType Layer type to get service for
   * @returns Service for the specified layer type
   */
  private getServiceForLayerType<T extends ValidLayerType>(
    layerType: T
  ): TwcLayerServiceInterface<LayerTypeMap[T]> {
    return this._twcArcgisType2PluginServiceMap[layerType];
  }

  /**
   * Check if a type string is a valid layer type
   * @param type Type string to check
   * @returns Whether the type is valid
   */
  private isValidLayerType(type: string): type is ValidLayerType {
    return TWC_LAYER_TYPES.includes(type as ValidLayerType);
  }

  /**
   * Create a plugin instance for a TWC product
   * @param twcProduct Product configuration
   * @param options Plugin options
   * @returns Observable that emits the created plugin
   */
  create(twcProduct: TwcProduct, options: PluginOptions): Observable<Plugin> {
    // Extract options with defaults
    const {
      legend,
      button,
      twcLayerTypes,
      maxRetryAttempts = this.DEFAULT_MAX_RETRY_ATTEMPTS,
      enableDebugLogging = false
    } = options;

    if (enableDebugLogging) {
      this._logger.trace(
        `[TwcRefreshLayerPluginService] Creating TWC layer plugin for product: ${twcProduct.id}`
      );
    }

    // Initialize or retrieve refresh subject for this product
    const refreshTrigger$ = this._getOrCreateRefreshSubject(twcProduct.id);
    const flightLevelValues = Object.keys(twcProduct.flightLevelProductIds);

    // Setup streams for various refresh triggers
    const timeIntervalStream$ = this._createTimeIntervalStream(twcProduct);
    const errorHandlingStream$ = this._setupErrorHandling(twcProduct);

    // Combine all refresh triggers into a single stream
    const refreshTriggers$ = this._combineRefreshTriggers(
      timeIntervalStream$,
      errorHandlingStream$,
      refreshTrigger$,
      enableDebugLogging ? twcProduct.id : undefined
    );

    // Create data stream with error handling and retries
    const twcDataStream$ = refreshTriggers$.pipe(
      switchMap(() =>
        this._fetchTwcData(twcProduct, flightLevelValues, maxRetryAttempts)
      ),
      shareReplay(1)
    );

    // Create the appropriate layer(s)
    const twcLayer$ = twcDataStream$.pipe(
      switchMap((twcUpdateData) =>
        this._createInitialLayer(twcUpdateData, twcLayerTypes)
      ),
      take(1),
      shareReplay(1)
    );

    // Load the layer and set up updates
    const twcLoadedLayer$ = twcLayer$.pipe(
      switchMap((layer) => layer.load()),
      shareReplay(1)
    );

    // Setup layer cleanup and initial metadata
    twcLoadedLayer$.pipe(take(1)).subscribe((layer) => {
      this._setupLayerCleanup(layer, twcProduct.id);
      this._initializeLayerMetadata(layer, twcProduct.id);
    });

    // Setup layer updates
    this._setupLayerUpdates(twcLoadedLayer$, twcDataStream$);

    // Return the plugin configuration
    return twcLayer$.pipe(
      map((layer) => this._createPlugin(layer, twcProduct.id, button, legend))
    );
  }

  /**
   * Get or create a refresh subject for a product
   * @param productId Product ID
   * @returns BehaviorSubject for triggering refreshes
   */
  private _getOrCreateRefreshSubject(productId: string): BehaviorSubject<void> {
    if (!this._refreshOperations.has(productId)) {
      this._refreshOperations.set(
        productId,
        new BehaviorSubject<void>(undefined)
      );
    }
    return this._refreshOperations.get(productId)!;
  }

  /**
   * Create time interval stream with visibility pausing
   * @param twcProduct Product to create stream for
   * @returns Observable emitting time values
   */
  private _createTimeIntervalStream(
    twcProduct: TwcProduct
  ): Observable<number> {
    return this._ToolkitApiService.mapReady$.pipe(
      switchMap((map) =>
        buildPauseOnVisiblitilyTsInterval$(twcProduct, map).pipe(
          takeUntilDestroyed(this._destroyRef)
        )
      )
    );
  }

  /**
   * Setup error handling for a product
   * @param twcProduct Product to handle errors for
   * @returns Observable for error events
   */
  private _setupErrorHandling(
    twcProduct: TwcProduct
  ): Observable<EsriRequestError | null> {
    return this._ToolkitApiService
      .registerEsriErrorHandler(twcProduct.defaultProductId, {
        excludeErrorTypes: [EsriErrorType.ABORT],
        debounceTime: 1000
      })
      .pipe(
        tap((error: EsriRequestError) => {
          this._logger.warn(
            `[TwcRefreshLayerPluginService] Error detected for product ${twcProduct.defaultProductId}:`,
            error
          );
          this._logger.info(
            `[TwcRefreshLayerPluginService] Triggering refresh for product ${twcProduct.defaultProductId}`
          );
        }),
        startWith(null)
      );
  }

  /**
   * Combine all refresh triggers into a single stream
   */
  private _combineRefreshTriggers(
    timeIntervalStream$: Observable<number>,
    errorHandlingStream$: Observable<EsriRequestError | null>,
    refreshTrigger$: BehaviorSubject<void>,
    debugProductId?: string
  ): Observable<any> {
    return combineLatest([
      timeIntervalStream$,
      errorHandlingStream$,
      refreshTrigger$.pipe(startWith(undefined))
    ]).pipe(
      tap(() => {
        if (debugProductId) {
          this._logger.trace(`[TwcRefreshLayerPluginService] Refresh triggered for ${debugProductId}`);
        }
      }),
      takeUntilDestroyed(this._destroyRef)
    );
  }

  /**
   * Fetch TWC data with proper error handling
   * @param twcProduct Product to fetch data for
   * @param flightLevelValues Flight levels to include
   * @param maxRetryAttempts Maximum number of retry attempts
   * @returns Observable of TWC response data
   */
  private _fetchTwcData(
    twcProduct: TwcProduct,
    flightLevelValues: string[],
    maxRetryAttempts: number
  ): Observable<TwcResponseData> {
    const currentTime = Math.floor(Date.now() / 1000);

    return this._twcDataProtocolService[twcProduct.protocol]
      .getTwcData$({
        updatedTimeInSeconds: currentTime,
        twcProduct,
        flightLevelValues
      })
      .pipe(
        tap((responseData) => {
          // Update time validation cache from response data
          if (responseData.tsInSeconds && responseData.timeMap) {
            this._timeValidationService.updateFromTimeMap(
              twcProduct.defaultProductId,
              responseData.tsInSeconds,
              responseData.timeMap
            );
          }
        }),
        retry({
          count: maxRetryAttempts,
          delay: this._createRetryStrategy(twcProduct.id, maxRetryAttempts)
        }),
        catchError((error) => {
          this._logger.error(
            `[TwcRefreshLayerPluginService] Failed to fetch data for ${twcProduct.id} after ${maxRetryAttempts} attempts:`,
            error
          );
          return throwError(
            () => new Error(`Failed to fetch TWC data: ${error.message}`)
          );
        })
      );
  }

  /**
   * Create retry strategy function
   */
  private _createRetryStrategy(productId: string, maxRetryAttempts: number) {
    return (error: any, retryCount: number) => {
      // Skip retry for certain error types
      if (error.name === 'AbortError') {
        this._logger.trace(`[TwcRefreshLayerPluginService] Skipping retry for AbortError (${productId})`);
        return throwError(() => error);
      }

      // Use exponential backoff
      const delayMs = Math.pow(2, retryCount) * 1000;
      this._logger.trace(
        `[TwcRefreshLayerPluginService] Retry ${retryCount + 1}/${maxRetryAttempts} for ${productId} after ${delayMs}ms`
      );
      return timer(delayMs);
    };
  }

  /**
   * Create the initial layer(s) based on configuration
   */
  private _createInitialLayer(
    twcUpdateData: TwcResponseData,
    layerTypes: ValidLayerType[]
  ): Observable<TwcLayerType> {
    if (layerTypes.length === 1) {
      return this._createLayer$(twcUpdateData, layerTypes[0]);
    } else {
      return this._createGroupLayer$(twcUpdateData, layerTypes);
    }
  }

  /**
   * Initialize layer metadata
   */
  private _initializeLayerMetadata(
    layer: TwcLayerType,
    productId: string
  ): void {
    layer.metadata = {
      productId: productId,
      createdAt: new Date().toISOString()
    };
  }

  /**
   * Create plugin instance from layer and options
   */
  private _createPlugin(
    layer: TwcLayerType,
    id: string,
    button?: ButtonParams,
    legend?: LegendParams
  ): Plugin {
    return new Plugin({
      id,
      layer,
      button,
      legend
    });
  }

  /**
   * Setup update mechanism for a layer
   * @param twcLoadedLayer$ Observable of the loaded layer
   * @param twcDataStream$ Observable of data updates
   */
  private _setupLayerUpdates(
    twcLoadedLayer$: Observable<TwcLayerType>,
    twcDataStream$: Observable<TwcResponseData>
  ): void {
    // Create the update stream
    const layerUpdateStream$ = this._createLayerUpdateStream(
      twcLoadedLayer$,
      twcDataStream$
    );

    // Subscribe to updates and apply them to the layer
    layerUpdateStream$
      .pipe(
        map(({ layer, twcUpdateData }) =>
          this._updateLayer(layer, twcUpdateData)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (error) => {
          this._logger.error(`[TwcRefreshLayerPluginService] Error updating layer:`, error);
        }
      });
  }

  /**
   * Create the layer update stream
   */
  private _createLayerUpdateStream(
    twcLoadedLayer$: Observable<TwcLayerType>,
    twcDataStream$: Observable<TwcResponseData>
  ) {
    return combineLatest({
      layer: twcLoadedLayer$,
      responseData: twcDataStream$
    }).pipe(
      switchMap(({ layer, responseData }) => {
        // Update metadata with latest timestamp
        layer.metadata = {
          ...layer.metadata,
          updatedAt: responseData.updatedTimeInSeconds
        };

        // Return combined stream with selected states
        return combineLatest({
          layer: of(layer),
          selectedFlightLevel: layer.selectedFlightLevel$,
          selectedTime: layer.selectedTime$,
          responseData: of(responseData)
        });
      }),
      // Skip the initial emission which just sets up the layer
      skip(1),
      // Transform to the format needed for updates
      map(({ layer, selectedFlightLevel, selectedTime, responseData }) => ({
        layer,
        twcUpdateData: {
          selectedFlightLevel,
          selectedTime,
          ...responseData
        } as TwcUpdateData
      }))
    );
  }

  /**
   * Update a layer with new data
   */
  private _updateLayer(
    layer: TwcLayerType,
    twcUpdateData: TwcUpdateData
  ): TwcLayerType {
    return layer instanceof TwcGroupLayer
      ? this._updateGroupLayer(layer, twcUpdateData)
      : this._updateSpecificLayer(layer as TwcSpecificLayer, twcUpdateData);
  }

  /**
   * Setup cleanup for when a layer is destroyed
   * @param layer Layer to set up cleanup for
   * @param productId ID of the associated product
   */
  private _setupLayerCleanup(layer: TwcLayerType, productId: string): void {
    layer.on('layerview-destroy', () => {
      this._logger.trace(`[TwcRefreshLayerPluginService] Layer ${layer.id} destroyed, cleaning up resources`);

      // Clean up refresh operations
      this._cleanupRefreshOperations(productId);

      // Unregister error handlers
      this._ToolkitApiService.unregisterEsriErrorHandler(productId);
    });
  }

  /**
   * Clean up refresh operations for a product
   */
  private _cleanupRefreshOperations(productId: string): void {
    if (this._refreshOperations.has(productId)) {
      const subject = this._refreshOperations.get(productId);
      subject?.complete();
      this._refreshOperations.delete(productId);
    }
  }

  /**
   * Trigger a manual refresh for a product
   * @param productId ID of the product to refresh
   */
  public triggerRefresh(productId: string): void {
    const refreshSubject = this._refreshOperations.get(productId);
    if (refreshSubject) {
      this._logger.trace(`[TwcRefreshLayerPluginService] Manually triggering refresh for ${productId}`);
      refreshSubject.next();
    } else {
      this._logger.warn(`[TwcRefreshLayerPluginService] No refresh operation found for product: ${productId}`);
    }
  }

  /**
   * Create a single layer for a specific type
   * @param twcData Data to create layer from
   * @param layerType Type of layer to create
   * @returns Observable of the created layer
   */
  private _createLayer$<T extends ValidLayerType>(
    twcData: TwcResponseData,
    layerType: T
  ): Observable<LayerTypeMap[T]> {
    this._logger.trace(
      `[TwcRefreshLayerPluginService] Creating ${layerType} layer for ${twcData.twcProduct.id}`
    );
    const service = this.getServiceForLayerType(layerType);
    return service.createLayer$(twcData).pipe(
      catchError((error) => {
        this._logger.error(`[TwcRefreshLayerPluginService] Error creating ${layerType} layer:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a group layer containing multiple layer types
   * @param responseData Data to create layers from
   * @param layerTypes Types of layers to include in the group
   * @returns Observable of the created group layer
   */
  private _createGroupLayer$(
    { timeMap, flightLevelValues, twcProduct, ...rest }: TwcResponseData,
    layerTypes: ValidLayerType[]
  ): Observable<TwcGroupLayer> {
    this._logger.trace(
      `[TwcRefreshLayerPluginService] Creating group layer with types [${layerTypes.join(', ')}] for ${twcProduct.id}`
    );

    // Create each layer in the group
    const layers$ = layerTypes.map((layerType) =>
      this._createLayer$(
        {
          timeMap,
          flightLevelValues,
          ...rest,
          twcProduct: {
            ...twcProduct,
            id: `${twcProduct.id}_${layerType}` as any
          }
        },
        layerType
      )
    );

    // Combine all layers into a group
    return combineLatest(layers$).pipe(
      map((layers) => {
        const layer = new TwcGroupLayer({
          id: twcProduct.id,
          zOrder: twcProduct.zOrder,
          label: twcProduct.label,
          selectedFlightLevel: initialFlightLevel(flightLevelValues),
          selectedTime: initialTime(timeMap),
          layers
        });

        layer.flightLevelValues.set(flightLevelValues);
        layer.timeMap.set(timeMap);

        return layer;
      }),
      catchError((error) => {
        this._logger.error(`[TwcRefreshLayerPluginService] Error creating group layer:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update all layers in a group with new data
   * @param groupLayer Group layer to update
   * @param twcData New data to apply
   * @returns Updated group layer
   */
  private _updateGroupLayer(
    groupLayer: TwcGroupLayer,
    twcData: TwcUpdateData
  ): TwcGroupLayer {
    const selectedFlightLevel = normalizeSelectedFlightLevel(
      twcData.flightLevelValues,
      twcData.selectedFlightLevel,
      groupLayer.selectedFlightLevel
    );
    const selectedTime = normalizeSelectedTime(
      twcData.timeMap,
      twcData.selectedTime,
      groupLayer.selectedTime
    );

    groupLayer.updateFlightLevels(
      twcData.flightLevelValues,
      selectedFlightLevel
    );
    groupLayer.updateTimeMap(twcData.timeMap, selectedTime);

    for (const layer of groupLayer.layers) {
      const layerType = layer.type as ValidLayerType;
      if (this.isValidLayerType(layerType)) {
        this._updateSpecificLayer(layer as TwcSpecificLayer, {
          ...twcData,
          selectedFlightLevel,
          selectedTime
        });
      }
    }
    return groupLayer;
  }

  /**
   * Update a specific layer with new data
   * @param layer Layer to update
   * @param twcData New data to apply
   * @returns Updated layer
   */
  private _updateSpecificLayer<T extends TwcSpecificLayer>(
    layer: T,
    twcData: TwcUpdateData
  ): T {
    if (this.isValidLayerType(layer.type)) {
      type LayerType = T extends { type: infer Type }
        ? Type extends ValidLayerType
          ? LayerTypeMap[Type]
          : never
        : never;

      try {
        const service = this.getServiceForLayerType(layer.type);
        service.updateLayer(layer as LayerType, twcData);
      } catch (error) {
        this._logger.error(`[TwcRefreshLayerPluginService] Error updating ${layer.type} layer:`, error);
      }
    }
    return layer;
  }
}
